import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Quote, Star } from "lucide-react";
import { Chapter, Reveal, EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Card = ({ t }) => (
  <div
    className="relative h-full bg-zinc-900 border border-zinc-800 p-8 md:p-10 flex flex-col"
    data-testid={`testimonial-card-${t.id}`}
  >
    <Quote className="w-7 h-7 text-primary mb-6" strokeWidth={1.5} />
    <p className="text-zinc-300 leading-relaxed text-sm md:text-base flex-1">
      "{t.quote}"
    </p>
    <div className="mt-8 flex items-center gap-4 pt-6 border-t border-white/10">
      <span
        className={`w-11 h-11 shrink-0 flex items-center justify-center font-heading font-bold text-sm tracking-wide ${
          t.kind === "client"
            ? "bg-primary/15 text-primary border border-primary/30"
            : "bg-sky-400/10 text-sky-400 border border-sky-400/30"
        }`}
        aria-hidden="true"
      >
        {t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-white text-sm truncate">{t.name}</p>
        <p className="text-xs text-zinc-500 truncate">
          {t.role} · {t.company}
        </p>
      </div>
      <div className="flex gap-0.5" aria-label={`${t.rating} star rating`}>
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} className="w-3.5 h-3.5 fill-primary text-primary" />
        ))}
      </div>
    </div>
  </div>
);

export default function Testimonials() {
  const [tab, setTab] = useState("client");
  const [data, setData] = useState({ client: [], candidate: [] });

  useEffect(() => {
    axios
      .get(`${API}/testimonials`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []);

  const list = data[tab] || [];

  return (
    <section id="stories" className="py-24 md:py-32 border-t border-white/10" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <Chapter num="Chapter 05" label="Stories" />
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight font-medium text-white max-w-2xl">
            Trusted on both sides of the table.
          </h2>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div className="inline-flex border border-white/10" data-testid="testimonial-tabs">
            {[
              { key: "client", label: "Client Feedback" },
              { key: "candidate", label: "Candidate Feedback" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-6 md:px-8 py-3.5 text-sm font-semibold transition-colors duration-300 ${
                  tab === t.key
                    ? "bg-primary text-primary-foreground"
                    : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`testimonial-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
              data-testid={`testimonial-grid-${tab}`}
            >
              {list.map((t) => (
                <Card key={t.id} t={t} />
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
